import { toNumberOrZero } from '~/utils/functions';
import { ProcessoContexto } from '../protocols';
import { processaIbsCbs } from './processaIbsCbs';
import { calculaImpostosIbsCbs } from './calculaImpostosIbsCbs';
import { getValorTabela } from './getValorTabela';

interface AlimentaDadosProps {
  itensParaProcessar: any;
  ctx: Omit<ProcessoContexto, 'qryProdutoCodIbsCbs'>;
  codIbsCbsInput?: number | null;
  isItemEditing?: boolean;
}

/**
 * Enriquece item(ns) com dados IBS/CBS preservando o formato de entrada
 * e acrescenta o cálculo de impostos (impostosIbsCbs) por item.
 *
 * - Se entrar 1 item → retorna 1 item.
 * - Se entrar array  → retorna array na mesma ordem.
 *
 * @param dados.itensParaProcessar Item ou array de itens a processar.
 * @param dados.ctx Contexto do processo (empresa, loja, cliente etc).
 * @param dados.codIbsCbsInput Código IBS/CBS opcional (fallback quando item não tiver).
 * @returns Item enriquecido (ou array) com campo adicional `impostosIbsCbs`.
 */
export const alimentaDadosIBSCBSCliente = async (
  dados: AlimentaDadosProps,
): Promise<any> => {
  const { itensParaProcessar, ctx, codIbsCbsInput, isItemEditing } =
    dados ?? ({} as AlimentaDadosProps);

  // Se nada foi passado, apenas retorna como veio (null/undefined)
  if (
    itensParaProcessar === null ||
    typeof itensParaProcessar === 'undefined'
  ) {
    return itensParaProcessar;
  }

  const sharedPayload = {
    cbxLocalConsumoIndex: ctx?.cbxLocalConsumoIndex,
    loja: {
      codCidade: toNumberOrZero(ctx?.loja?.codCidade),
      estado: ctx?.loja?.estado,
    },
    empresa: ctx?.empresa,
    cliMestreCodCidade: ctx?.cliMestreCodCidade,
    cliCidadeCodCidade: ctx?.cliCidadeCodCidade,
  };

  const lista = Array.isArray(itensParaProcessar)
    ? itensParaProcessar
    : [itensParaProcessar];

  const enrich = async (pt: any) => {
    const codIbscbs = isItemEditing
      ? codIbsCbsInput ?? pt?.cod_ibs_cbs
      : pt?.cod_ibs_cbs ?? codIbsCbsInput ?? null;

    const resultado = await processaIbsCbs({
      qryProdutoCodIbsCbs: codIbscbs || null,
      ...sharedPayload,
    });

    // Merge do item original + enriquecimento IBS/CBS
    const merged = { ...(pt ?? {}), ...resultado };

    // Cálculo de impostos IBS/CBS por item
    const impostosIbsCbs = calculaImpostosIbsCbs(
      {
        per_aliq_cbs: toNumberOrZero(merged.per_aliq_cbs),
        per_aliq_ibs_est: toNumberOrZero(merged.per_aliq_ibs_est),
        per_aliq_ibs_mun: toNumberOrZero(merged.per_aliq_ibs_mun),
        per_red_cbs: toNumberOrZero(merged.per_red_cbs),
        per_red_ibs: toNumberOrZero(merged.per_red_ibs),
        val_red_social: toNumberOrZero(merged.val_red_social),
      },
      toNumberOrZero(getValorTabela(merged)),
      toNumberOrZero(merged.val_icms) + toNumberOrZero(merged.val_fcp_icms),
      toNumberOrZero(merged.val_pis) + toNumberOrZero(merged.val_cofins),
      toNumberOrZero(codIbscbs),
    );

    return { ...merged, ...impostosIbsCbs };
  };

  const enriquecidos = await Promise.all(lista.map(enrich));

  return Array.isArray(itensParaProcessar) ? enriquecidos : enriquecidos[0];
};
